import axios from 'axios';
import authService from '../services/auth.service';
import {
    PLANNING_TASK_PRIORITY_LABELS,
    PLANNING_TASK_STATUT_LABELS,
    PLANNING_TASK_TYPE_LABELS,
    PLANNING_TASK_TYPES,
} from './equipePlanningConstants';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export const hydraMember = (res) => res?.data?.['hydra:member'] || res?.data?.member || res?.data || [];

export const extractId = (ref) => {
    if (ref == null) return null;
    if (typeof ref === 'number') return Number.isFinite(ref) ? ref : null;
    if (typeof ref === 'object') {
        if (ref.id != null) return Number(ref.id);
        if (typeof ref['@id'] === 'string') {
            const m = ref['@id'].match(/\/(\d+)$/);
            return m ? parseInt(m[1], 10) : null;
        }
    }
    if (typeof ref === 'string') {
        const m = ref.match(/\/(\d+)$/);
        if (m) return parseInt(m[1], 10);
        // Id nu renvoyé par certains payloads ("5")
        if (/^\d+$/.test(ref)) return parseInt(ref, 10);
        return null;
    }
    return null;
};

export const extractEntityIds = (user) => {
    if (!user) return [];
    const primaryId = user.entityReference?.id ?? user.entity_reference_id ?? null;
    const primary = primaryId ? [Number(primaryId)] : [];
    const secondary = (user.entitesSecondaires || []).map((e) => Number(extractId(e)));
    return [...new Set([...primary, ...secondary.filter(Number.isFinite)])];
};

export const userLabel = (u) => {
    if (!u) return '—';
    const name = `${u.prenom || ''} ${u.nom || ''}`.trim();
    return name || u.email || `Utilisateur #${u.id}`;
};

/** Initiales ou prénom court pour colonne équipe compacte. */
export const userCompactLabel = (u) => {
    if (!u) return '—';
    const prenom = `${u.prenom || ''}`.trim();
    const nom = `${u.nom || ''}`.trim();
    if (prenom && nom) {
        if (prenom.length <= 4) return prenom;
        return `${prenom.charAt(0)}${nom.charAt(0)}`.toUpperCase();
    }
    const name = `${prenom} ${nom}`.trim();
    if (name.length <= 6) return name;
    return name.slice(0, 5) + '…';
};

export const formatPlanningDate = (raw) => {
    if (!raw) return null;
    const iso = typeof raw === 'string' ? raw.slice(0, 10) : null;
    if (!iso) return null;
    const d = parseDayIso(iso);
    if (!d) return iso;
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const userEntityId = (u) => extractId(u?.entityReference ?? u?.entity_reference_id);

export const fmtDayIso = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

export const parseDayIso = (iso) => {
    if (!iso || typeof iso !== 'string') return null;
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
};

export const addDays = (d, n) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
};

export const startOfWeekMonday = (d) => {
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const dow = x.getDay();
    const diff = dow === 0 ? -6 : 1 - dow;
    x.setDate(x.getDate() + diff);
    return x;
};

/** Numéro de semaine ISO 8601 (1–53) pour une Date. */
export const isoWeekNumber = (input) => {
    const d = new Date(Date.UTC(input.getFullYear(), input.getMonth(), input.getDate()));
    const dayNum = d.getUTCDay() || 7; // lundi=1 … dimanche=7
    d.setUTCDate(d.getUTCDate() + 4 - dayNum); // jeudi de la semaine courante
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

/** @param {'1w'|'2w'|'1m'} mode */
export function buildDayColumns(mode, anchorDate) {
    const start = startOfWeekMonday(anchorDate);
    const count = mode === '1m' ? 28 : mode === '1w' ? 7 : 14;
    const cols = [];
    for (let i = 0; i < count; i += 1) {
        const d = addDays(start, i);
        cols.push({
            iso: fmtDayIso(d),
            date: d,
            weekday: d.toLocaleDateString('fr-FR', { weekday: 'short' }),
            dayNum: d.getDate(),
            monthShort: d.toLocaleDateString('fr-FR', { month: 'short' }),
        });
    }
    return cols;
}

/* ── Jours fériés français ── */
function easterSunday(year) {
    // Algorithme de Meeus/Jones/Butcher
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
}

function frenchHolidays(year) {
    const easter = easterSunday(year);
    const map = {};
    const add = (date, name) => { map[fmtDayIso(date)] = name; };
    add(new Date(year, 0, 1), 'Jour de l’an');
    add(addDays(easter, 1), 'Lundi de Pâques');
    add(new Date(year, 4, 1), 'Fête du Travail');
    add(new Date(year, 4, 8), 'Victoire 1945');
    add(addDays(easter, 39), 'Ascension');
    add(addDays(easter, 50), 'Lundi de Pentecôte');
    add(new Date(year, 6, 14), 'Fête nationale');
    add(new Date(year, 7, 15), 'Assomption');
    add(new Date(year, 10, 1), 'Toussaint');
    add(new Date(year, 10, 11), 'Armistice 1918');
    add(new Date(year, 11, 25), 'Noël');
    return map;
}

const holidayCache = {};

/** Nom du jour férié pour une date ISO (yyyy-mm-dd), ou null. */
export function holidayNameFor(iso) {
    const year = Number(String(iso).slice(0, 4));
    if (!Number.isFinite(year)) return null;
    if (!holidayCache[year]) holidayCache[year] = frenchHolidays(year);
    return holidayCache[year][iso] || null;
}

/* ── Absences (CP / RTT / maladie) ── */
export const ABSENCE_TYPE_LABELS = {
    cp: 'Congés payés',
    rtt: 'RTT',
    maladie: 'Arrêt maladie',
    temps_partiel: 'Temps partiel (jour non travaillé)',
    autre: 'Autre absence',
};

export const ABSENCE_TYPE_SHORT = {
    cp: 'CP',
    rtt: 'RTT',
    maladie: 'Maladie',
    temps_partiel: 'Tps partiel',
    autre: 'Absent',
};

/** Jours de la semaine pour la récurrence temps partiel (ISO : 1 = lundi). */
export const WEEKDAY_LABELS = {
    1: 'Lundi',
    2: 'Mardi',
    3: 'Mercredi',
    4: 'Jeudi',
    5: 'Vendredi',
    6: 'Samedi',
    7: 'Dimanche',
};

/** Jour ISO (1 = lundi … 7 = dimanche) d'une date YYYY-MM-DD. */
export function isoWeekdayOf(iso) {
    const d = new Date(`${iso}T12:00:00`).getDay();
    return d === 0 ? 7 : d;
}

/** Une absence couvre-t-elle ce jour ? Gère la récurrence hebdo (jourSemaine). */
export function absenceCoversDay(absence, iso) {
    if (iso < absence.dateDebut || iso > absence.dateFin) return false;
    if (absence.jourSemaine != null) return isoWeekdayOf(iso) === absence.jourSemaine;
    return true;
}

export function normalizeAbsenceRow(row) {
    return {
        ...row,
        id: row.id,
        utilisateurId: extractId(row.utilisateur),
        typeAbsence: row.typeAbsence || 'cp',
        jourSemaine: row.jourSemaine ?? null,
        dateDebut: String(row.dateDebut || '').slice(0, 10),
        dateFin: String(row.dateFin || '').slice(0, 10),
        note: row.note || '',
    };
}

/** Absences chevauchant la période [rangeStart, rangeEnd]. */
export async function fetchAbsences({ rangeStart, rangeEnd }) {
    const params = { pagination: false };
    if (rangeStart) params['dateFin[after]'] = rangeStart;
    if (rangeEnd) params['dateDebut[before]'] = rangeEnd;
    const res = await axios.get(`${API_URL}/equipe_absences`, {
        withCredentials: true,
        headers: { Accept: 'application/ld+json' },
        params,
    });
    return hydraMember(res).map(normalizeAbsenceRow);
}

export async function createAbsence({ utilisateurId, typeAbsence, dateDebut, dateFin, note, jourSemaine = null }) {
    const body = {
        utilisateur: `/api/users/${utilisateurId}`,
        typeAbsence,
        dateDebut,
        dateFin,
        note: note?.trim() || null,
        jourSemaine: jourSemaine != null ? Number(jourSemaine) : null,
    };
    const res = await axios.post(`${API_URL}/equipe_absences`, body, {
        withCredentials: true,
        headers: { Accept: 'application/ld+json', 'Content-Type': 'application/ld+json' },
    });
    return normalizeAbsenceRow(res.data);
}

export async function deleteAbsence(id) {
    await axios.delete(`${API_URL}/equipe_absences/${id}`, {
        withCredentials: true,
        headers: { Accept: 'application/ld+json' },
    });
}

/** map[userId][iso] = absence couvrant ce jour. */
export function groupAbsencesByUserDay(absences, userIds, dayIsos) {
    const map = {};
    userIds.forEach((uid) => { map[String(uid)] = {}; });
    absences.forEach((a) => {
        const uid = String(a.utilisateurId);
        if (!map[uid]) return;
        dayIsos.forEach((iso) => {
            if (absenceCoversDay(a, iso) && !map[uid][iso]) {
                map[uid][iso] = a;
            }
        });
    });
    return map;
}

export const taskTypeClass = (type) => {
    if (PLANNING_TASK_TYPES.includes(type)) return type;
    return 'projet';
};

export const taskCardTitle = (task) => {
    if (task.typeTache === 'note') {
        return task.nomStandClient || task.noteParProjet || 'Note';
    }
    if (task.numeroErp) return task.numeroErp;
    if (task.nomStandClient) return task.nomStandClient;
    if (task.salonNom) return task.salonNom;
    return 'Tâche';
};

export const taskCardSubtitle = (task) => {
    const parts = [];
    if (task.nomStandClient && task.numeroErp) parts.push(task.nomStandClient);
    if (task.salonNom) parts.push(task.salonNom);
    return parts.join(' · ') || task.nomCommercial || '';
};

/** Libellé court affiché sur la carte compacte (manager). */
export const taskCardCompactLabel = (task) => {
    if (task.typeTache === 'note') {
        const s = task.nomStandClient || task.noteParProjet || 'Note';
        return s.length > 16 ? `${s.slice(0, 15)}…` : s;
    }
    if (task.numeroErp) return task.numeroErp;
    if (task.nomStandClient) {
        const s = task.nomStandClient;
        return s.length > 16 ? `${s.slice(0, 15)}…` : s;
    }
    if (task.salonNom) {
        const s = task.salonNom;
        return s.length > 16 ? `${s.slice(0, 15)}…` : s;
    }
    return '—';
};

/** Lignes label / valeur pour infobulle au survol. */
export const buildTaskTooltipRows = (task) => {
    if (!task) return [];
    const rows = [];
    const push = (label, val) => {
        if (val != null && val !== '') rows.push({ label, value: String(val) });
    };
    push('Type', PLANNING_TASK_TYPE_LABELS[task.typeTache] || task.typeTache);
    if (task.priorite && task.priorite !== 'normale') {
        // accent => la bulle affiche un chip coloré au lieu d'un texte simple
        rows.push({
            label: 'Priorité',
            value: PLANNING_TASK_PRIORITY_LABELS[task.priorite] || task.priorite,
            accent: task.priorite,
        });
    }
    if (task.statutAvancement && task.statutAvancement !== 'a_faire') {
        rows.push({
            label: 'Avancement',
            value: PLANNING_TASK_STATUT_LABELS[task.statutAvancement] || task.statutAvancement,
            accent: `st-${task.statutAvancement}`,
        });
    }
    if (task.majEmployeAt) {
        const d = new Date(task.majEmployeAt);
        const when = Number.isFinite(d.getTime())
            ? d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
            : '';
        const nature = task.majEmployeType === 'deplacement' ? 'déplacée' : 'statut modifié';
        push('Par l’employé', `${nature}${when ? ` · ${when}` : ''}`);
    }
    push('Commercial', task.nomCommercial);
    push('N° ERP', task.numeroErp);
    push('Date rendu', formatPlanningDate(task.dateRendu));
    push('Stand client', task.nomStandClient);
    push('Salon', task.salonNom);
    if (task.surface != null && task.surface !== '') push('Surface', `${task.surface} m²`);
    if (task.budget != null && task.budget !== '') push('Budget', `${task.budget} € HT`);
    push('Note', task.noteParProjet);
    push('Brief complet', task.briefComplet ? 'Oui' : null);
    if (task.jourAssigne) push('Jour assigné', formatPlanningDate(task.jourAssigne));
    // Tâche venant d'un autre board (collaborateur invité) : on précise l'entité
    if (task._externe) push('Entité', task._entiteNom);
    return rows;
};

export async function fetchCurrentUserFresh() {
    const cached = authService.getCurrentUser();
    if (!cached?.id) return null;
    try {
        const res = await axios.get(`${API_URL}/users/${cached.id}`, {
            withCredentials: true,
            headers: { Accept: 'application/ld+json' },
        });
        return res.data;
    } catch {
        return cached;
    }
}

export async function fetchAllUsers() {
    const res = await axios.get(`${API_URL}/users`, {
        withCredentials: true,
        headers: { Accept: 'application/ld+json' },
        params: { pagination: false },
    });
    return hydraMember(res);
}

export async function fetchAllEntities() {
    const res = await axios.get(`${API_URL}/entities`, {
        withCredentials: true,
        headers: { Accept: 'application/ld+json' },
        params: { pagination: false },
    });
    return hydraMember(res);
}

export async function fetchAllProjets() {
    const res = await axios.get(`${API_URL}/projets`, {
        withCredentials: true,
        headers: { Accept: 'application/ld+json' },
        params: { pagination: false },
    });
    return hydraMember(res);
}

export async function fetchAllClients() {
    const res = await axios.get(`${API_URL}/clients`, {
        withCredentials: true,
        headers: { Accept: 'application/ld+json' },
        params: { pagination: false },
    });
    return hydraMember(res);
}

export async function fetchAllSalons() {
    const res = await axios.get(`${API_URL}/salons`, {
        withCredentials: true,
        headers: { Accept: 'application/ld+json' },
        params: { pagination: false },
    });
    return hydraMember(res);
}

export async function fetchPlanningTasks(filters) {
    const params = { pagination: false, 'order[jourAssigne]': 'asc', 'order[positionJour]': 'asc' };
    if (filters.entiteId) params.entite = filters.entiteId;
    if (filters.assigneA) params.assigneA = filters.assigneA;
    // Plusieurs assignés d'un coup (axios sérialise en assigneA[]=1&assigneA[]=2 → IN SQL)
    if (filters.assigneAIn?.length) params.assigneA = filters.assigneAIn;
    if (filters.rangeStart) params['jourAssigne[after]'] = filters.rangeStart;
    if (filters.rangeEnd) params['jourAssigne[before]'] = filters.rangeEnd;

    const res = await axios.get(`${API_URL}/equipe_planning_tasks`, {
        withCredentials: true,
        headers: { Accept: 'application/ld+json' },
        params,
    });
    return hydraMember(res);
}

export function normalizeTaskRow(row) {
    const jourRaw = row.jourAssigne;
    let jourAssigne = null;
    if (jourRaw != null && typeof jourRaw === 'string' && jourRaw.length >= 10) {
        jourAssigne = jourRaw.slice(0, 10);
    } else if (jourRaw != null) {
        jourAssigne = jourRaw;
    }
    return {
        ...row,
        id: row.id,
        typeTache: row.typeTache || 'projet',
        priorite: row.priorite || 'normale',
        statutAvancement: row.statutAvancement || 'a_faire',
        majEmployeAt: row.majEmployeAt || null,
        majEmployeType: row.majEmployeType || null,
        assigneAId: extractId(row.assigneA),
        entiteId: extractId(row.entite),
        jourAssigne,
        positionJour: Number(row.positionJour) || 0,
        sourceProjetId: extractId(row.sourceProjet),
    };
}

export function isTaskAssigned(t) {
    return t?.assigneAId != null && Boolean(t?.jourAssigne);
}

export function isTaskUnassigned(t) {
    return !isTaskAssigned(t);
}

export function groupTasksByUserDay(tasks, userIds, dayIsos) {
    /** @type {Record<string, Record<string, typeof tasks>>} */
    const map = {};
    userIds.forEach((uid) => {
        map[String(uid)] = {};
        dayIsos.forEach((iso) => { map[String(uid)][iso] = []; });
    });
    tasks.forEach((t) => {
        const uid = String(t.assigneAId);
        const iso = t.jourAssigne;
        if (!map[uid] || !map[uid][iso]) return;
        map[uid][iso].push(t);
    });
    Object.keys(map).forEach((uid) => {
        dayIsos.forEach((iso) => {
            map[uid][iso].sort((a, b) => a.positionJour - b.positionJour || a.id - b.id);
        });
    });
    return map;
}

export function buildTaskApiPayload(form, assigneAId = null, jourAssigne = null, positionJour = 0) {
    const body = {
        typeTache: form.typeTache || 'projet',
        priorite: form.priorite || 'normale',
        nomCommercial: form.nomCommercial?.trim() || null,
        entite: `/api/entities/${form.entiteId}`,
        numeroErp: form.numeroErp?.trim() || null,
        dateRendu: form.dateRendu || null,
        nomStandClient: form.nomStandClient?.trim() || null,
        salonNom: form.salonNom?.trim() || null,
        surface: form.surface?.trim() || null,
        budget: form.budget?.trim() || null,
        noteParProjet: form.noteParProjet?.trim() || null,
        briefComplet: Boolean(form.briefComplet),
        positionJour,
    };
    if (assigneAId != null && jourAssigne) {
        body.assigneA = `/api/users/${assigneAId}`;
        body.jourAssigne = jourAssigne;
    }
    if (form.sourceProjet) {
        body.sourceProjet = `/api/projets/${form.sourceProjet}`;
    }
    return body;
}

export const EMPTY_TASK_FORM = {
    typeTache: 'projet',
    priorite: 'normale',
    nomCommercial: '',
    entiteId: '',
    numeroErp: '',
    dateRendu: '',
    nomStandClient: '',
    salonNom: '',
    surface: '',
    budget: '',
    noteParProjet: '',
    briefComplet: false,
    sourceProjet: null,
    assigneAId: '',
    jourAssigne: fmtDayIso(new Date()),
};

export async function createPlanningTask(form) {
    const body = buildTaskApiPayload(form);
    const res = await axios.post(`${API_URL}/equipe_planning_tasks`, body, {
        withCredentials: true,
        headers: { Accept: 'application/ld+json', 'Content-Type': 'application/ld+json' },
    });
    return normalizeTaskRow(res.data);
}

export async function patchPlanningTask(id, patch) {
    const res = await axios.patch(`${API_URL}/equipe_planning_tasks/${id}`, patch, {
        withCredentials: true,
        headers: {
            Accept: 'application/ld+json',
            'Content-Type': 'application/merge-patch+json',
        },
    });
    return normalizeTaskRow(res.data);
}

export async function deletePlanningTask(id) {
    await axios.delete(`${API_URL}/equipe_planning_tasks/${id}`, {
        withCredentials: true,
        headers: { Accept: 'application/ld+json' },
    });
}

/**
 * Duplique une tâche (mêmes infos) en l'assignant directement à un jour.
 * Utilisé par le Ctrl/Alt + drag pour poser une tâche sur plusieurs jours.
 */
export async function duplicatePlanningTask(task, assigneAId, jourAssigne, positionJour = 0) {
    const body = {
        typeTache: task.typeTache || 'projet',
        priorite: task.priorite || 'normale',
        nomCommercial: task.nomCommercial || null,
        entite: `/api/entities/${task.entiteId}`,
        numeroErp: task.numeroErp || null,
        dateRendu: task.dateRendu || null,
        nomStandClient: task.nomStandClient || null,
        salonNom: task.salonNom || null,
        surface: task.surface || null,
        budget: task.budget || null,
        noteParProjet: task.noteParProjet || null,
        briefComplet: Boolean(task.briefComplet),
        positionJour,
    };
    if (assigneAId != null && jourAssigne) {
        body.assigneA = `/api/users/${assigneAId}`;
        body.jourAssigne = jourAssigne;
    }
    if (task.sourceProjetId) {
        body.sourceProjet = `/api/projets/${task.sourceProjetId}`;
    }
    const res = await axios.post(`${API_URL}/equipe_planning_tasks`, body, {
        withCredentials: true,
        headers: { Accept: 'application/ld+json', 'Content-Type': 'application/ld+json' },
    });
    return normalizeTaskRow(res.data);
}

export async function movePlanningTask(task, assigneAId, jourAssigne, positionJour = 0) {
    // Action manager : elle « acquitte » l'éventuel changement fait par l'employé.
    if (assigneAId == null || !jourAssigne) {
        return patchPlanningTask(task.id, {
            assigneA: null,
            jourAssigne: null,
            positionJour: 0,
            majEmployeAt: null,
            majEmployeType: null,
        });
    }
    return patchPlanningTask(task.id, {
        assigneA: `/api/users/${assigneAId}`,
        jourAssigne,
        positionJour,
        majEmployeAt: null,
        majEmployeType: null,
    });
}

/**
 * Déplacement fait par l'employé assigné sur son propre planning :
 * même assigné, autre jour — et on trace le changement pour le manager.
 */
export async function moveOwnTaskParEmploye(task, jourAssigne, positionJour = 0) {
    return patchPlanningTask(task.id, {
        jourAssigne,
        positionJour,
        majEmployeAt: new Date().toISOString(),
        majEmployeType: 'deplacement',
    });
}

/** Changement d'avancement fait par l'employé assigné (à faire / en cours / terminée / en retard). */
export async function setOwnTaskStatutParEmploye(task, statutAvancement) {
    return patchPlanningTask(task.id, {
        statutAvancement,
        majEmployeAt: new Date().toISOString(),
        majEmployeType: 'statut',
    });
}

/** Fenêtre du bac à tâches : non assignées + tâches actives sur les 90 derniers jours (et futur). */
export const BACKLOG_WINDOW_DAYS = 90;

function sortBacklogRows(rows) {
    // Non assignées en premier, puis les plus récentes.
    return rows.sort((a, b) => {
        const ua = isTaskAssigned(a) ? 1 : 0;
        const ub = isTaskAssigned(b) ? 1 : 0;
        if (ua !== ub) return ua - ub;
        return (b.id || 0) - (a.id || 0);
    });
}

/**
 * Charge tâches assignées (période visible) + bac pour une entité.
 * Le bac est borné côté serveur : tâches non assignées (jourAssigne null,
 * incluses grâce au DateFilter INCLUDE_NULL) + tâches des 90 derniers jours.
 * Cas courant (période visible dans la fenêtre) : une seule requête.
 */
export async function fetchPlanningTasksSplit(entiteId, rangeStart, rangeEnd) {
    const backlogStart = fmtDayIso(addDays(new Date(), -BACKLOG_WINDOW_DAYS));

    if (rangeStart >= backlogStart) {
        const rows = (await fetchPlanningTasks({ entiteId, rangeStart: backlogStart })).map(normalizeTaskRow);
        const backlog = sortBacklogRows([...rows]);
        const assigned = rows.filter((t) => isTaskAssigned(t)
            && t.jourAssigne >= rangeStart && t.jourAssigne <= rangeEnd);
        return { backlog, assigned };
    }

    // Navigation dans un passé antérieur à la fenêtre du bac : requête dédiée pour la grille.
    const [backlogRows, rangeRows] = await Promise.all([
        fetchPlanningTasks({ entiteId, rangeStart: backlogStart }),
        fetchPlanningTasks({ entiteId, rangeStart, rangeEnd }),
    ]);
    const backlog = sortBacklogRows(backlogRows.map(normalizeTaskRow));
    const assigned = rangeRows.map(normalizeTaskRow).filter(isTaskAssigned);
    return { backlog, assigned };
}

/* ═══════════════════════════════════════════════════════════════════════
   Collaborateurs invités (cross-entité)
   Un invité = un utilisateur d'une autre entité dont la ligne apparaît sur
   le board de l'entité hôte. Les tâches qu'on lui assigne restent des
   tâches de l'entité hôte ; son propre board les affiche en « miroir ».
   ═══════════════════════════════════════════════════════════════════════ */

export function normalizeInviteRow(row) {
    return {
        id: row.id,
        entiteId: extractId(row.entite),
        utilisateurId: extractId(row.utilisateur),
    };
}

/** Invitations actives sur le board d'une entité. */
export async function fetchInvites(entiteId) {
    const res = await axios.get(`${API_URL}/equipe_planning_invites`, {
        withCredentials: true,
        headers: { Accept: 'application/ld+json' },
        params: { pagination: false, entite: entiteId },
    });
    return hydraMember(res).map(normalizeInviteRow);
}

export async function createInvite(entiteId, utilisateurId) {
    const res = await axios.post(`${API_URL}/equipe_planning_invites`, {
        entite: `/api/entities/${entiteId}`,
        utilisateur: `/api/users/${utilisateurId}`,
    }, {
        withCredentials: true,
        headers: { Accept: 'application/ld+json', 'Content-Type': 'application/ld+json' },
    });
    return normalizeInviteRow(res.data);
}

export async function deleteInvite(id) {
    await axios.delete(`${API_URL}/equipe_planning_invites/${id}`, {
        withCredentials: true,
        headers: { Accept: 'application/ld+json' },
    });
}

/**
 * True si la tâche appartient à une autre entité que le board affiché.
 * Important : un entiteId manquant (null) ne doit PAS être traité comme externe,
 * sinon une tâche locale se retrouve verrouillée / en double après un move.
 */
export function isTaskExternalToBoard(task, boardEntiteId) {
    if (!task || boardEntiteId == null || boardEntiteId === '') return false;
    if (task._externe) return true;
    if (task.entiteId == null) return false;
    return String(task.entiteId) !== String(boardEntiteId);
}

/**
 * Marque une tâche comme « externe » au board affiché : elle appartient à
 * une autre entité (membre invité ailleurs). Affichée avec un badge ;
 * déplaçable pour réassigner, mais le contenu s'édite depuis le board source.
 */
export function tagExternalTask(row, entitesById) {
    const ent = entitesById?.[String(row.entiteId)];
    return {
        ...row,
        _externe: true,
        _entiteNom: ent?.nom || 'Autre entité',
        _entiteCouleur: ent?.couleur || '#6366f1',
    };
}

/** Retire les flags miroir (après un move sur le board propriétaire). */
export function stripExternalTaskFlags(row) {
    if (!row || !row._externe) return row;
    const next = { ...row };
    delete next._externe;
    delete next._entiteNom;
    delete next._entiteCouleur;
    return next;
}

/**
 * Tâches « miroir » : assignées aux membres listés mais portées par une
 * AUTRE entité que celle du board (collaborateur invité sur un autre board).
 * Elles montrent que le membre est pris — aucune double réservation possible.
 */
export async function fetchExternalTasksForUsers(userIds, entiteId, rangeStart, rangeEnd) {
    if (!userIds?.length) return [];
    const rows = await fetchPlanningTasks({ assigneAIn: userIds, rangeStart, rangeEnd });
    return rows
        .map(normalizeTaskRow)
        .filter((t) => isTaskAssigned(t) && String(t.entiteId) !== String(entiteId));
}
